import React, { useEffect, useState, useCallback } from 'react';
import { Inbox, CalendarDays, LogOut, Loader2, ShieldCheck, Mail } from 'lucide-react';
import { api, getToken, clearToken } from './api';
import PanelLogin from './PanelLogin';
import DemoRequestsTab from './DemoRequestsTab';
import AppointmentsTab from './AppointmentsTab';
import RequestDetailPage from './RequestDetailPage';
import AdminsTab from './AdminsTab';
import MailPanelTab from './MailPanelTab';

/** Regroupe un tableau plat de demandes par adresse email (insensible à la casse). */
function groupRequestsByEmail(requests) {
  const map = {};
  for (const r of requests) {
    const key = (r.email || '').toLowerCase().trim() || `__no_email__${r.id}`;
    if (!map[key]) {
      map[key] = { email: r.email || '', requests: [] };
    }
    map[key].requests.push(r);
  }

  return Object.values(map)
    .map((g) => {
      // Trier par date décroissante (demande la plus récente en premier)
      const sorted = [...g.requests].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      const latest = sorted[0];
      return {
        email: g.email,
        displayName: latest.nom || '—',
        telephone: latest.telephone || '',
        societe: latest.societe || '',
        latestDate: latest.created_at,
        latestStatus: latest.statut,
        totalNotes: sorted.reduce((sum, r) => sum + (r.panel_notes_history?.length || 0), 0),
        requests: sorted,
      };
    })
    // Trier les groupes par date de la demande la plus récente
    .sort((a, b) => new Date(b.latestDate || 0) - new Date(a.latestDate || 0));
}

export default function PapaEnMoussePanel() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('requests');
  const [prefill, setPrefill] = useState(null);

  // État de la liste des demandes (géré ici pour persister lors du retour)
  const [requests, setRequests] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Email du groupe sélectionné (null = liste, string = page de détail)
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    document.title = 'Espace interne — PitBoard';
    const token = getToken();
    if (!token) { setChecking(false); return; }
    api('/me')
      .then((data) => setAdmin(data))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await api('/demo-requests');
      setRequests(data.requests);
      setStatuses(data.statuses);
    } catch { /* noop */ } finally { setLoadingRequests(false); }
  }, []);

  // Charger les demandes dès que l'admin est authentifié
  useEffect(() => {
    if (admin) loadRequests();
  }, [admin, loadRequests]);

  const logout = () => { clearToken(); setAdmin(null); };

  const schedule = (data) => { setPrefill(data); setTab('appointments'); setSelectedEmail(null); };

  // Depuis la liste RDV → naviguer vers la fiche du groupe lié
  const goToRequest = useCallback((appointment) => {
    const ref = appointment.request_ref || '';
    const email = (appointment.email || '').toLowerCase().trim();

    let foundEmail = null;

    if (ref) {
      const [src, id] = ref.split(':');
      const found = requests.find((r) => r.id === id && r.source === src);
      if (found) foundEmail = (found.email || '').toLowerCase().trim();
    }
    if (!foundEmail && email) {
      const found = requests.find((r) => (r.email || '').toLowerCase() === email);
      if (found) foundEmail = email;
    }

    if (foundEmail) {
      setTab('requests');
      setSelectedEmail(foundEmail);
    }
  }, [requests]);

  const changeStatus = async (r, status) => {
    setRequests((prev) => prev.map((x) =>
      x.id === r.id && x.source === r.source ? { ...x, statut: status } : x
    ));
    try { await api(`/demo-requests/${r.source}/${r.id}`, { method: 'PATCH', body: { status } }); }
    catch { loadRequests(); }
  };

  // Quand une note est ajoutée depuis la page de détail → mettre à jour la liste en fond
  const handleNoteAddedInList = (reqId, source, newNote) => {
    setRequests((prev) => prev.map((x) =>
      x.id === reqId && x.source === source
        ? { ...x, panel_notes_history: [...(x.panel_notes_history || []), newNote] }
        : x
    ));
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!admin) return <PanelLogin onAuth={(a) => { setAdmin(a); }} />;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => { setTab(id); setSelectedEmail(null); }}
      data-testid={`papa-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors shrink-0 whitespace-nowrap ${tab === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  // Groupes calculés depuis le tableau plat (recalculé à chaque mise à jour de requests)
  const groups = groupRequestsByEmail(requests);

  // Groupe sélectionné dérivé de l'email
  const selectedGroup = selectedEmail
    ? groups.find((g) => g.email.toLowerCase() === selectedEmail.toLowerCase()) ?? null
    : null;

  const showDetail = tab === 'requests' && selectedGroup !== null;

  return (
    <div className="min-h-screen bg-white" data-testid="papa-panel">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-lg font-extrabold text-slate-900 tracking-tight">PitBoard<span className="text-sky-500">.</span></span>
            <span className="text-xs text-slate-400 hidden sm:inline">Espace interne</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="papa-admin-email">{admin.email}</span>
            <button onClick={logout} data-testid="papa-logout" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600">
              <LogOut size={16} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-8 py-6">

        {/* Navigation onglets — toujours visible */}
        <div className="flex items-center gap-2 mb-6 bg-white rounded-xl border border-slate-200 p-1.5 w-fit max-w-full overflow-x-auto">
          <TabBtn id="requests" icon={Inbox} label="Demandes de démo" />
          <TabBtn id="appointments" icon={CalendarDays} label="Rendez-vous" />
          <TabBtn id="admins" icon={ShieldCheck} label="Admins" />
          <TabBtn id="mail" icon={Mail} label="Mail" />
        </div>

        {/* Contenu */}
        {tab === 'mail' && (
          <MailPanelTab />
        )}

        {tab === 'appointments' && (
          <AppointmentsTab
            prefill={prefill}
            onPrefillConsumed={() => setPrefill(null)}
            onGoToRequest={goToRequest}
            currentAdmin={admin}
          />
        )}

        {tab === 'admins' && (
          <AdminsTab currentAdmin={admin} />
        )}

        {tab === 'requests' && !showDetail && (
          <DemoRequestsTab
            groups={groups}
            statuses={statuses}
            loading={loadingRequests}
            onRefresh={loadRequests}
            onChangeStatus={changeStatus}
            onSchedule={schedule}
            onSelectGroup={(g) => setSelectedEmail(g.email)}
          />
        )}

        {showDetail && (
          <RequestDetailPage
            requests={selectedGroup.requests}
            admin={admin}
            onBack={() => setSelectedEmail(null)}
            onSchedule={schedule}
            onStatusChange={(id, source, status) => changeStatus({ id, source }, status)}
            onNoteSaved={handleNoteAddedInList}
          />
        )}
      </div>
    </div>
  );
}
